/* CSS/SVG cross-section diagram of a solar array showing piles, panels,
   ground vegetation and grazing animals — illustrates dual use of land. */

export default function SiteAndPanels() {
  return (
    <section className="bg-[#F2EFE8] border-y border-[#E0DCD3]" data-testid="site-and-panels-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 reveal">
            <p className="overline mb-4">Site & panels</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight mb-5">
              The fields keep their character — just with panels above them.
            </h2>
            <p className="text-[15px] leading-relaxed text-[#2A2B2A]/85 mb-4">
              Panels are mounted on slim <strong>steel piles</strong> driven into
              the ground. Rows are angled and spaced so
              sunlight reaches the ground, allowing vegetation to grow and low‑level
              grazing to continue.
            </p>
            <p className="text-[15px] leading-relaxed text-[#2A2B2A]/85">
              Hedgerows and treelines are retained as natural screening, and the
              noisiest equipment (transformer / inverter stations) is located away
              from residential properties to preserve the quiet rural setting.
            </p>

            <ul className="mt-7 space-y-2.5 text-[14px]">
              {[
                "Approx. 3.0 m maximum height — well below tree and hedgerow level",
                "Approx. 0.8 m ground clearance — keeps the soil and vegetation alive",
                "Steel piles — fully removable at end of life",
                "Mature hedgerows (~4 m) retained as natural screening",
                "Set‑backs from homes & roads, low‑level grazing continues",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`panel-feature-${i}`}>
                  <span className="mt-2 inline-block h-1.5 w-1.5 rounded-sm bg-[#CBA365]" />
                  <span className="text-[#2A2B2A]/85">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="rounded-sm border border-[#E0DCD3] bg-white overflow-hidden">
              <div className="aspect-[16/10] relative">
                <svg viewBox="0 0 800 500" className="w-full h-full block">
                  {/* sky */}
                  <defs>
                    <linearGradient id="sky" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#EEEAE0" />
                      <stop offset="100%" stopColor="#F9F8F6" />
                    </linearGradient>
                    <linearGradient id="ground" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#8C9B86" />
                      <stop offset="100%" stopColor="#4C4538" />
                    </linearGradient>
                  </defs>
                  <rect x="0" y="0" width="800" height="320" fill="url(#sky)" />
                  {/* distant hedgerow silhouette */}
                  <path d="M0 305 Q 100 290 180 300 T 360 295 T 540 300 T 720 295 T 800 300 L 800 320 L 0 320 Z" fill="#8C9B86" opacity="0.55" />
                  {/* sun */}
                  <circle cx="650" cy="90" r="32" fill="#CBA365" opacity="0.65" />
                  <circle cx="650" cy="90" r="46" fill="#CBA365" opacity="0.18" />

                  {/* ground */}
                  <rect x="0" y="320" width="800" height="180" fill="url(#ground)" />
                  {/* grass strokes */}
                  {Array.from({ length: 90 }).map((_, i) => (
                    <line key={i} x1={i * 9 + 4} y1={322} x2={i * 9 + 6} y2={313} stroke="#3A4B3A" strokeWidth="0.8" opacity="0.7" />
                  ))}

                  {/* Panels — three rows. baseY=296 → panel top at y=230 (=3.0m) and panel low at y=296 (=0.8m above ground y=320), correctly proportional. */}
                  {[
                    { x: 80, baseY: 296 },
                    { x: 330, baseY: 296 },
                    { x: 580, baseY: 296 },
                  ].map((row, idx) => (
                    <g key={idx} className="diagram-panel">
                      {/* posts */}
                      <line x1={row.x + 10} y1={row.baseY - 5} x2={row.x + 10} y2={row.baseY + 28} stroke="#4C4538" strokeWidth="3" />
                      <line x1={row.x + 110} y1={row.baseY - 66} x2={row.x + 110} y2={row.baseY + 28} stroke="#4C4538" strokeWidth="3" />
                      {/* panel surface */}
                      <polygon
                        points={`${row.x},${row.baseY} ${row.x + 130},${row.baseY - 66} ${row.x + 138},${row.baseY - 58} ${row.x + 8},${row.baseY + 8}`}
                        fill="#2A3B2A"
                      />
                      {/* highlight */}
                      <polygon
                        points={`${row.x + 8},${row.baseY - 2} ${row.x + 132},${row.baseY - 64} ${row.x + 134},${row.baseY - 62} ${row.x + 10},${row.baseY}`}
                        fill="#7A8E76"
                        opacity="0.85"
                      />
                      {/* panel grid lines */}
                      {[0.2, 0.4, 0.6, 0.8].map((p) => (
                        <line
                          key={p}
                          x1={row.x + 130 * p}
                          y1={row.baseY - 66 * p}
                          x2={row.x + 10 + 124 * p}
                          y2={row.baseY + 8 - 74 * p}
                          stroke="#1F2A1F"
                          strokeWidth="0.6"
                          opacity="0.6"
                        />
                      ))}
                    </g>
                  ))}

                  {/* sheep silhouettes */}
                  {[200, 470, 720].map((cx, i) => (
                    <g key={i} transform={`translate(${cx} 360)`}>
                      <ellipse cx="0" cy="0" rx="18" ry="11" fill="#F2EFE8" />
                      <circle cx="-15" cy="-2" r="6" fill="#2A2B2A" />
                      <line x1="-8" y1="9" x2="-8" y2="16" stroke="#2A2B2A" strokeWidth="2" />
                      <line x1="6" y1="9" x2="6" y2="16" stroke="#2A2B2A" strokeWidth="2" />
                    </g>
                  ))}

                  {/* ===== HEIGHT DIMENSIONS ===== */}
                  {/* Max panel height (3.0 m) — full-height dimension left of leftmost panel */}
                  <g stroke="#4C4538" strokeWidth="0.7" fill="none">
                    <line x1="210" y1="230" x2="58" y2="230" strokeDasharray="2 3" opacity="0.55" />
                    <line x1="80" y1="320" x2="58" y2="320" strokeDasharray="2 3" opacity="0.55" />
                    <line x1="65" y1="232" x2="65" y2="318" />
                  </g>
                  <polygon points="61,239 69,239 65,231" fill="#4C4538" />
                  <polygon points="61,311 69,311 65,319" fill="#4C4538" />
                  <g transform="translate(42 275) rotate(-90)">
                    <rect x="-22" y="-9" width="44" height="18" rx="1" fill="#F9F8F6" stroke="#E0DCD3" strokeWidth="0.6" />
                    <text x="0" y="3" textAnchor="middle" fontFamily="Work Sans, sans-serif" fontSize="11" fontWeight="700" fill="#3A4B3A">≈ 3.0 m</text>
                  </g>

                  {/* Ground clearance (0.8 m) — SHORT dimension only inside the gap, with leader to label */}
                  <g stroke="#4C4538" strokeWidth="0.7" fill="none">
                    <line x1="80" y1="296" x2="74" y2="296" />
                    <line x1="80" y1="320" x2="74" y2="320" />
                    <line x1="77" y1="298" x2="77" y2="318" />
                  </g>
                  <polygon points="74,302 80,302 77,297" fill="#4C4538" />
                  <polygon points="74,314 80,314 77,319" fill="#4C4538" />
                  {/* leader line out to label, threading through the gap between rows */}
                  <line x1="78" y1="308" x2="244" y2="312" stroke="#4C4538" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.55" />
                  <g transform="translate(244 312)">
                    <rect x="0" y="-11" width="64" height="22" rx="1" fill="#F9F8F6" stroke="#E0DCD3" strokeWidth="0.6" />
                    <text x="32" y="-1" textAnchor="middle" fontFamily="Work Sans, sans-serif" fontSize="10.5" fontWeight="700" fill="#3A4B3A">≈ 0.8 m</text>
                    <text x="32" y="8" textAnchor="middle" fontFamily="Work Sans, sans-serif" fontSize="8.5" fill="#63635E">ground clearance</text>
                  </g>

                  {/* Hedgerow height (~4m) — right side near distant hedgerow */}
                  <g stroke="#4C4538" strokeWidth="0.7" fill="none">
                    <line x1="760" y1="295" x2="760" y2="319" />
                  </g>
                  <polygon points="756,302 764,302 760,294" fill="#4C4538" />
                  <polygon points="756,313 764,313 760,320" fill="#4C4538" />
                  <g transform="translate(778 308)">
                    <rect x="0" y="-10" width="20" height="14" rx="1" fill="#F9F8F6" stroke="#E0DCD3" strokeWidth="0.6" />
                    <text x="10" y="0" textAnchor="middle" fontFamily="Work Sans, sans-serif" fontSize="9.5" fontWeight="700" fill="#3A4B3A">≈ 4 m</text>
                  </g>

                  {/* Annotation lines */}
                  <g stroke="#4C4538" strokeWidth="0.8">
                    <line x1="170" y1="200" x2="170" y2="170" />
                    <line x1="170" y1="170" x2="240" y2="170" />
                  </g>
                  <text x="248" y="168" fontFamily="Work Sans, sans-serif" fontSize="11" fill="#2A2B2A" fontWeight="600">Panel array</text>
                  <text x="248" y="183" fontFamily="Work Sans, sans-serif" fontSize="10" fill="#63635E">Mounted on steel piles</text>

                  <g stroke="#4C4538" strokeWidth="0.8">
                    <line x1="490" y1="335" x2="490" y2="410" />
                    <line x1="490" y1="410" x2="560" y2="410" />
                  </g>
                  <text x="568" y="408" fontFamily="Work Sans, sans-serif" fontSize="11" fill="#FFFFFF" fontWeight="600">Vegetation & low grazing</text>
                  <text x="568" y="423" fontFamily="Work Sans, sans-serif" fontSize="10" fill="#E0DCD3">Soil recovers, biodiversity grows</text>

                  <g stroke="#4C4538" strokeWidth="0.8">
                    <line x1="720" y1="270" x2="720" y2="230" />
                    <line x1="720" y1="230" x2="650" y2="230" />
                  </g>
                  <text x="640" y="228" textAnchor="end" fontFamily="Work Sans, sans-serif" fontSize="11" fill="#2A2B2A" fontWeight="600">Hedgerows retained</text>
                  <text x="640" y="243" textAnchor="end" fontFamily="Work Sans, sans-serif" fontSize="10" fill="#63635E">Natural screening from view</text>
                </svg>
              </div>
              <div className="border-t border-[#E0DCD3] bg-[#F9F8F6] px-5 py-3 text-[11px] tracking-[0.18em] uppercase font-semibold text-[#4C4538]">
                Indicative cross-section — typical heights shown
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
